<script setup lang="ts">
import IndividualHeader from '@/modules/investing/components/individual-header.vue'
import IndividualNav from '@/modules/investing/components/individual-nav.vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import TaxesList from '@/modules/attaching/components/taxes-list.vue'
import { useRoute } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import { useOtherEntityStore } from '@/modules/investing/stores/other-entity-store'
import { useInvestingInvestorStore } from '@/modules/investing/stores/investor-store'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { VButton, VDropdownImproved, VIcon, VSection, VTable } from '@/modules/shared/components'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { get } from 'lodash'
import { useDocumentStore } from '@/modules/tax/stores/document-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'

const route = useRoute()
const otherEntityStore = useOtherEntityStore()
const investorStore = useInvestingInvestorStore()
const individualStore = useIndividualStore()
const documentStore = useDocumentStore()

// Tax Documents
const profileOptions = computed(() => {
  const options = [
    {
      label: currentIndividual.value?.name,
      value: `Investor:${route.params.individual_id}`,
    },
  ]

  otherEntityStore.other_entities.forEach((other_entity) => {
    options.push({
      label: other_entity.name,
      value: `InvestorSet:${other_entity.id}`,
    })
  })

  return options
})
const viewingAs = computed(() => {
  if (selectedProfileKeys.value.length === profileOptions.value.length) {
    return 'all profiles'
  }

  if (selectedProfileKeys.value.length === 1) {
    return profileOptions.value.find((option) => option.value === selectedProfileKeys.value[0])?.name
  }

  if (selectedProfileKeys.value.length > 1 || selectedProfileKeys.value.length < 1) {
    return `${selectedProfileKeys.value.length} profiles`
  }
})

const default_year = new Date().getFullYear() - 1
const taxYearOptions = computed(() => {
  const years = [{ label: default_year, value: default_year }]
  const yearsAdded = [default_year]
  documents.value.forEach((document) => {
    if (!yearsAdded.includes(document.tax_year)) {
      years.push({ label: document.tax_year, value: document.tax_year })
      yearsAdded.push(document.tax_year)
    }
  })

  return years
})
const selectedYearsString = computed(() =>
  selectedYears.value.length > 0 ? selectedYears.value.join(', ') : '(not selected)',
)

const documents = computed(() => individualStore.listTaxDocuments)

const filteredDocuments = computed(() =>
  documents.value
    .filter((document) => selectedProfileKeys.value.includes(`${document.shareholder.type}:${document.shareholder.id}`))
    .filter((document) => selectedYears.value.includes(document.tax_year)),
)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const fetch = async (shareholder_cids = null) => {
  showSkeleton()
  // to remove when individual performance branch is merged
  await Promise.all([
    otherEntityStore.fetchOtherEntities('individual', route.params.individual_id),
    investorStore.fetchIndividual(route.params.individual_id),
  ])
  currentIndividual.value = investorStore.items.get(cid.value)

  await individualStore.fetchTaxDocuments(route.params.individual_id, {
    slug: route.params.slug,
    shareholder_cids: selectedCids.value,
  })
  hideSkeleton()
}
const deleteDocument = async (document) => {
  showSkeleton()
  await documentStore.destroy(document.uuid)
  hideSkeleton()
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const can_access_entities = computed(
  () =>
    route.params.individual_id === authStore.current_user.investor_id?.toString() || authStore.is_site_or_group_admin,
)
const isSiteAdmin = computed(() => authStore.is_site_admin)

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const cid = computed(() => `individual:${route.params.individual_id}`)
const currentIndividual = ref(null)

const selectedProfileKeys = ref([])
const selectedCids = computed(() => selectedProfileKeys.value)
const selectedYears = ref([])

onMounted(async () => {
  await fetch()
  selectedYears.value = [default_year]
  selectedProfileKeys.value = profileOptions.value.map((option) => option.value)
})
</script>

<template>
  <TheLayout>
    <IndividualHeader />
    <IndividualNav />
    <VSection>
      <div class="z-50 flex items-center justify-between">
        <div class="mb-6 flex items-center space-x-2">
          <VDropdownImproved v-model="selectedYears" aligned="left" :options="taxYearOptions">
            <VButton size="md">
              <div class="flex items-center space-x-2">
                <div>Filter by year {{ selectedYearsString }}</div>
                <VIcon name="chevron_selector_vertical" />
              </div>
            </VButton>
          </VDropdownImproved>
          <VDropdownImproved v-model="selectedProfileKeys" aligned="left" :options="profileOptions">
            <VButton size="md">
              <div class="flex items-center space-x-2">
                <div>Filter by {{ viewingAs }}</div>
                <VIcon name="chevron_selector_vertical" />
              </div>
            </VButton>
          </VDropdownImproved>
        </div>
      </div>

      <VTable
        :columns="[
          {
            key: 'actions',
            name: '',
            type: 'actions',
            align: 'left',
            is_visible: true,
          },
          {
            key: 'fileable.name',
            name: 'Entity',
            type: 'string',
            align: 'left',
            is_visible: true,
            sorted: true,
          },
          {
            key: 'shareholder.name',
            name: 'Shareholder',
            type: 'string',
            align: 'left',
            is_visible: false,
          },
          {
            key: 'attachment_original_file_name',
            name: 'File Name',
            type: 'string',
            align: 'left',
            is_visible: true,
          },
          {
            key: 'updated_at',
            name: 'Updated at',
            type: 'date',
            align: 'center',
            is_visible: true,
          },
          {
            key: 'tax_year',
            name: 'Year',
            type: 'string',
            align: 'center',
            is_visible: true,
          },
        ]"
        :items="filteredDocuments"
        :name="`${route.params.slug}-documents`"
        :skeleton="skeleton"
        :slots="['fileable.name', 'attachment_download_file_name', 'shareholder.name', 'actions']"
      >
        <template #fileable.name="{ item }">
          <div class="mb-1 flex w-72 items-center justify-between">
            <div>
              <RouterLink
                class="hyperlink"
                :to="{
                  name: 'investing.entity-overview',
                  params: { entity_id: get(item, 'fileable.id'), entity_type: get(item, 'fileable.type') },
                }"
                v-if="can_access_entities"
              >
                {{ get(item, 'fileable.name') }}
              </RouterLink>
              <template v-else>
                {{ get(item, 'fileable.name') }}
              </template>
            </div>
          </div>
        </template>
        <template #shareholder.name="{ item }">
          <p class="max-w-lg overflow-hidden text-ellipsis" :title="item.name">
            {{ get(item, 'shareholder.name') }}
          </p>
        </template>
        <template #attachment_download_file_name="{ item }">
          <p class="max-w-xl overflow-hidden text-ellipsis" :title="item.attachment_download_file_name">
            {{ item.attachment_download_file_name }}
          </p>
        </template>
        <template #actions="{ item }">
          <div class="flex space-x-1.5">
            <a :href="item.attachment_download_url" target="_blank">
              <VButton size="xs"> Download </VButton>
            </a>
            <a :href="item.attachment_view_url" target="_blank">
              <VButton size="xs"> View </VButton>
            </a>
            <VButton v-if="isSiteAdmin" size="xs" :click="() => deleteDocument(item)"> Delete </VButton>
          </div>
        </template>
      </VTable>
    </VSection>
  </TheLayout>
</template>
